import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Link as LinkIcon, ExternalLink, Copy, TrendingUp } from 'lucide-react';
import { authService, dashboardService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function AffiliateRevenuePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [dealSites, setDealSites] = useState([]);
  const [deals, setDeals] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [generatedLinks, setGeneratedLinks] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileData, networksData, sitesData, dealsData, productsData] = await Promise.all([
        authService.getProfile(),
        apiClient.get('/api/merchant/affiliate/networks'),
        apiClient.get('/api/merchant/affiliate/deal-sites'),
        apiClient.get('/api/dashboard/deals'),
        dashboardService.getProducts(100, 0)
      ]);
      setUser(profileData.member);
      setNetworks(networksData.data.networks || []);
      setDealSites(sitesData.data.sites || []);
      setProducts(productsData.products || []);
      
      // Join deals with product info
      const dealsWithProducts = (dealsData.data.deals || []).map(deal => {
        const product = (productsData.products || []).find(p => p._id === deal.product_id);
        return { ...deal, product };
      });
      setDeals(dealsWithProducts);
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoading(false);
    }
  };

  const getProductById = (productId) => {
    return products.find(p => p._id === productId);
  };

  const generateAffiliateLink = async (networkId) => {
    if (!selectedDeal) {
      toast.error('Please select a deal first');
      return;
    }

    try {
      const response = await apiClient.post('/api/merchant/affiliate/generate-link', {
        deal_id: selectedDeal._id,
        network: networkId,
        source: 'affiliate_page'
      });

      setGeneratedLinks({
        ...generatedLinks,
        [networkId]: response.data.affiliate_url
      });

      toast.success('Affiliate link generated!', {
        description: 'Link copied to clipboard'
      });

      navigator.clipboard.writeText(response.data.affiliate_url);
    } catch (error) {
      toast.error('Failed to generate link');
    }
  };

  const getSubmissionText = (site) => {
    if (!selectedDeal) return '';

    const product = selectedDeal.product || {};
    const price = product.price || 0;
    const msrp = product.msrp || price || price * 1.3; // Estimate MSRP if not available
    const savings = msrp - price;
    const productTitle = product.title || 'Great Deal';
    const merchantName = selectedDeal.merchant?.name || product.brand || 'Commerce Index';
    
    // Use generated affiliate link if available, otherwise use deal page link
    const dealLink = Object.values(generatedLinks)[0] || `${window.location.origin}/deal/${selectedDeal._id}`;

    if (site.id === 'slickdeals') {
      return `[${merchantName}] ${productTitle} $${price.toFixed(2)} (Reg $${msrp.toFixed(2)})

${selectedDeal.discount_value}% OFF - Save $${savings.toFixed(2)}
${selectedDeal.coupon_code ? `Code: ${selectedDeal.coupon_code}` : 'No code needed'}

CI Score: ${selectedDeal.ci_deal_score}/1000 (${selectedDeal.ci_deal_score >= 800 ? 'Excellent' : selectedDeal.ci_deal_score >= 600 ? 'Good' : 'Average'} Deal)

Link: ${dealLink}`;
    }

    return `${productTitle} - $${price.toFixed(2)} (Save $${savings.toFixed(2)})` + (selectedDeal.coupon_code ? ` - Code: ${selectedDeal.coupon_code}` : '');
  };

  const handleLogout = async () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
      <p className="text-[#9CB0C7]">Loading...</p>
    </div>;
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Affiliate Revenue</h1>
            <p className="text-sm text-[#9CB0C7]">Affiliate & Revenue Partnerships</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">← Dashboard</button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">Sign Out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Affiliate & Revenue Partnerships</h2>
          <p className="text-[#9CB0C7]">Maximize revenue through affiliate networks and deal site distribution</p>
        </div>

        {/* Deal Selector */}
        <div className="mb-8 rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
          <h3 className="text-xl font-semibold text-white mb-4">Select Deal to Promote</h3>
          <select
            value={selectedDeal?._id || ''}
            onChange={(e) => {
              const deal = deals.find(d => d._id === e.target.value);
              setSelectedDeal(deal);
              setGeneratedLinks({});
            }}
            className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60"
          >
            <option value="">Choose a deal...</option>
            {deals.map((deal) => (
              <option key={deal._id} value={deal._id}>
                {deal.product?.title || 'Deal'} - {deal.discount_value}% OFF - CI Score: {deal.ci_deal_score}
              </option>
            ))}
          </select>
          {selectedDeal && (
            <div className="mt-4 p-4 bg-[#111A2E] rounded-lg border border-[#22304A]">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#1A2437] rounded-lg overflow-hidden">
                  {selectedDeal.product?.image_url && (
                    <img src={selectedDeal.product.image_url} alt={selectedDeal.product.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">{selectedDeal.product?.title}</h4>
                  <p className="text-sm text-[#9CB0C7]">
                    ${selectedDeal.product?.price?.toFixed(2)} 
                    <span className="text-[#8CF78E] ml-2">Save ${((selectedDeal.product?.msrp || 0) - (selectedDeal.product?.price || 0)).toFixed(2)}</span>
                  </p>
                  <p className="text-xs text-[#9CB0C7] mt-1">CI Score: {selectedDeal.ci_deal_score}/1000</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Affiliate Networks */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold text-white mb-4">Generate Affiliate Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {networks.map((network) => (
              <div key={network.id} className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-white">{network.name}</h4>
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    network.status === 'ready' ? 'bg-[#8CF78E]/10 text-[#8CF78E]' : 'bg-[#9CB0C7]/10 text-[#9CB0C7]'
                  }`}>
                    {network.configured ? 'Configured' : 'Not Configured'}
                  </span>
                </div>
                <p className="text-sm text-[#9CB0C7] mb-4">
                  {network.status === 'ready' 
                    ? 'Generate trackable affiliate links' 
                    : 'Configure API keys in Settings to enable'}
                </p>
                {selectedDeal ? (
                  <>
                    <button
                      onClick={() => generateAffiliateLink(network.id)}
                      disabled={network.status !== 'ready'}
                      className="w-full py-2 bg-[#7A5CFF] text-white rounded-lg hover:bg-[#7A5CFF]/90 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                    >
                      Generate {network.name} Link
                    </button>
                    {generatedLinks[network.id] && (
                      <div className="mt-2 p-2 bg-[#111A2E] rounded border border-[#3EF1C7]/20">
                        <p className="text-xs text-[#9CB0C7] mb-1">Generated Link:</p>
                        <p className="text-xs text-[#3EF1C7] break-all font-mono">{generatedLinks[network.id]}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-4 bg-[#111A2E] rounded-lg border border-[#22304A]">
                    <p className="text-xs text-[#9CB0C7]">Select a deal above to generate links</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Top Deal Sites */}
        <div>
          <h3 className="text-xl font-semibold text-white mb-4">Submit to Top US Deal Sites</h3>
          {!selectedDeal ? (
            <div className="text-center py-12 rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
              <p className="text-[#9CB0C7] mb-2">Select a deal above to get started</p>
              <p className="text-sm text-[#9CB0C7]">You'll see submission templates and one-click posting</p>
            </div>
          ) : (
            <>
              <div className="mb-6 p-4 bg-[#3EF1C7]/10 border border-[#3EF1C7]/20 rounded-lg">
                <p className="text-sm text-[#3EF1C7] font-medium mb-2">💡 Recommended Workflow:</p>
                <ol className="text-sm text-[#9CB0C7] list-decimal list-inside space-y-1">
                  <li>First, <strong className="text-white">generate an affiliate link</strong> above (CJ, ShareASale, etc.)</li>
                  <li>The generated link will be <strong className="text-white">automatically included</strong> in your submission template below</li>
                  <li>Copy the template and submit to Slickdeals, RetailMeNot, etc.</li>
                  <li>Track clicks and conversions in your affiliate dashboard</li>
                </ol>
              </div>
              
              <p className="text-[#9CB0C7] mb-6">Submit <strong className="text-white">{selectedDeal.product?.title || 'this deal'}</strong> to these high-traffic platforms</p>
              
              <div className="space-y-4">
                {dealSites.map((site) => (
                  <div key={site.id} className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="text-lg font-semibold text-white">{site.name}</h4>
                          <span className={`px-2 py-1 text-xs rounded ${
                            site.traffic === 'Very High' ? 'bg-[#8CF78E]/10 text-[#8CF78E]' :
                            site.traffic === 'High' ? 'bg-[#3EF1C7]/10 text-[#3EF1C7]' :
                            'bg-[#FFC857]/10 text-[#FFC857]'
                          }`}>
                            {site.traffic} Traffic
                          </span>
                        </div>
                        
                        {/* Pre-filled Submission Text */}
                        <div className="mb-3 p-3 bg-[#111A2E] rounded-lg border border-[#22304A]">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-[#9CB0C7]">Submission Template:</p>
                            <button
                              onClick={() => {
                                const text = getSubmissionText(site);
                                navigator.clipboard.writeText(text);
                                toast.success('Template copied!');
                              }}
                              className="text-xs text-[#3EF1C7] hover:text-[#3EF1C7]/80 flex items-center gap-1"
                            >
                              <Copy className="w-3 h-3" />
                              Copy
                            </button>
                          </div>
                          <pre className="text-xs text-white whitespace-pre-wrap font-mono">
                            {getSubmissionText(site)}
                          </pre>
                        </div>
                        
                        <p className="text-sm text-[#9CB0C7] mb-3">{site.notes}</p>
                        
                        <div className="flex gap-3">
                          <button
                            onClick={() => window.open(site.submission_url, '_blank')}
                            className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] rounded-lg hover:bg-[#3EF1C7]/90 transition-colors text-sm font-semibold flex items-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open {site.name} Submission
                          </button>
                          <button
                            onClick={() => {
                              const text = getSubmissionText(site);
                              navigator.clipboard.writeText(text);
                              toast.success('Copied! Now paste in submission form');
                            }}
                            className="px-4 py-2 bg-[#111A2E] border border-[#22304A] text-white rounded-lg hover:bg-[#1A2437] transition-colors text-sm flex items-center gap-2"
                          >
                            <Copy className="w-4 h-4" />
                            Copy Template
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 p-6 bg-[#7A5CFF]/10 border border-[#7A5CFF]/20 rounded-lg">
          <h4 className="text-white font-semibold mb-2">💡 Maximize Your Reach</h4>
          <p className="text-sm text-[#9CB0C7] mb-3">
            Submit your deals to these platforms to reach millions of deal hunters. Each platform has unique submission requirements and review processes.
          </p>
          <p className="text-sm text-[#9CB0C7]">
            <strong className="text-white">Pro Tip:</strong> Use affiliate links from CJ, ShareASale, or Rakuten to earn commissions on external traffic.
          </p>
        </div>
      </main>
    </div>
  );
}
